import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ExternalLink, Linkedin, X, MapPin, CalendarDays, MessageSquare } from "lucide-react";
import { api, imageUrl, ACCENTS } from "@/lib/api";
import VentureLogo from "@/components/VentureLogo";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Reveal, StatusBadge } from "@/components/site/Section";

const VentureDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [venture, setVenture] = useState(null);
  const [missing, setMissing] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/ventures").then((r) => {
      const found = r.data.find((v) => v.slug === slug);
      if (found) setVenture(found);
      else setMissing(true);
    }).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="venture-not-found">
        <p className="font-display text-3xl text-slate-300">This venture could not be found.</p>
        <Link to="/" className="rounded-full bg-[#E2C08D] px-6 py-3 text-sm font-semibold text-[#0A0C10]" data-testid="detail-back-home">Back to Home</Link>
      </div>
    );
  }

  if (!venture) return <div className="min-h-screen" />;

  const a = ACCENTS[venture.accent] || ACCENTS.gold;

  return (
    <main className="bg-[#0A0C10] min-h-screen" data-testid="venture-detail-page">
      <Header />
      <section className="relative pt-36 pb-16 hero-grid-bg overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 hover:text-[#E2C08D] transition-colors" data-testid="detail-back-link">
            <ArrowLeft size={13} /> All Ventures
          </Link>
          <div className="mt-10 flex flex-col md:flex-row md:items-end gap-8 md:gap-12">
            <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} data-testid="detail-logo">
              <VentureLogo v={venture} size="h-28 w-28 md:h-36 md:w-36" />
            </motion.div>
            <div className="flex-1">
              <div className="mask-line"><motion.h1 initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="font-display text-5xl md:text-7xl font-semibold text-slate-100 tracking-tight" data-testid="detail-name">{venture.name}</motion.h1></div>
              <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.7 }} className={`mt-3 font-display italic text-xl md:text-2xl ${a.text}`}>{venture.tagline}</motion.p>
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }} className="mt-5 flex flex-wrap items-center gap-3">
                <StatusBadge accent={venture.accent}>{venture.status}</StatusBadge>
                {venture.sector && <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">{venture.sector}</span>}
                {venture.role && <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#E2C08D]/80">{venture.role}</span>}
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.7 }} className="mt-7 flex flex-wrap gap-3">
                {venture.website && (
                  <a href={venture.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#E2C08D] px-6 py-3 text-sm font-semibold text-[#0A0C10] hover:bg-[#f0d3a4] transition-colors" data-testid="detail-website-link">
                    Visit Website <ExternalLink size={14} />
                  </a>
                )}
                {venture.linkedin && (
                  <a href={venture.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-[#38BDF8]/60 hover:text-[#38BDF8] transition-colors" data-testid="detail-linkedin-link">
                    LinkedIn <Linkedin size={14} />
                  </a>
                )}
                <button onClick={() => navigate("/", { state: { scrollTo: "impact" } })} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-slate-100 hover:border-[#E2C08D]/60 hover:text-[#E2C08D] transition-colors" data-testid="detail-contact-cta">
                  <MessageSquare size={14} /> Discuss This Venture
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid gap-14 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E2C08D]">The Story</span>
              <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed" data-testid="detail-story">{venture.description}</p>
            </Reveal>
            {venture.milestones?.length > 0 && (
              <Reveal delay={0.1}>
                <div className="mt-14" data-testid="detail-milestones">
                  <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E2C08D]">Journey So Far</span>
                  <div className="relative mt-7 space-y-6">
                    <div className="absolute left-[7px] top-1 bottom-1 w-px bg-white/10" />
                    {venture.milestones.map((m, i) => (
                      <div key={i} className="relative flex items-start gap-5 pl-1">
                        <span className="relative z-10 mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#E2C08D] bg-[#0A0C10]" />
                        <p className="text-slate-300 leading-relaxed">{m}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
          <div className="space-y-6">
            {venture.highlights?.length > 0 && (
              <Reveal delay={0.05}>
                <div className="rounded-2xl border border-white/8 bg-[#10141d] p-7" data-testid="detail-highlights">
                  <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E2C08D]">Highlights</span>
                  <ul className="mt-5 space-y-3">
                    {venture.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-3 text-sm text-slate-300">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: a.dot }} /> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
            {venture.investors?.length > 0 && (
              <Reveal delay={0.1}>
                <div className="rounded-2xl border border-white/8 bg-[#10141d] p-7" data-testid="detail-investors">
                  <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E2C08D]">Investors</span>
                  <ul className="mt-5 space-y-3">
                    {venture.investors.map((inv) => (
                      <li key={inv} className="text-sm text-slate-200 font-medium border-b border-white/5 pb-3 last:border-0 last:pb-0">{inv}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
            {venture.team?.length > 0 && (
              <Reveal delay={0.15}>
                <div className="rounded-2xl border border-white/8 bg-[#10141d] p-7" data-testid="detail-team">
                  <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E2C08D]">Team</span>
                  <ul className="mt-5 space-y-3">
                    {venture.team.map((t) => (
                      <li key={t} className="text-sm text-slate-200 font-medium border-b border-white/5 pb-3 last:border-0 last:pb-0">{t}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
          </div>
        </div>

        {venture.gallery?.length > 0 && (
          <div className="mx-auto max-w-7xl px-5 md:px-8 mt-8" data-testid="detail-gallery">
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E2C08D]">Gallery</span>
              <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {venture.gallery.map((url, i) => (
                  <button key={i} onClick={() => setLightbox(url)} className="group overflow-hidden rounded-2xl border border-white/8" data-testid={`detail-gallery-item-${i}`}>
                    <img src={imageUrl(url)} alt={`${venture.name} moment ${i + 1}`} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </button>
                ))}
              </div>
            </Reveal>
          </div>
        )}
      </section>
      <Footer />

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-5" onClick={() => setLightbox(null)} data-testid="detail-lightbox">
            <button className="absolute top-5 right-5 text-slate-300 hover:text-white" aria-label="Close"><X size={26} /></button>
            <img src={imageUrl(lightbox)} alt="" className="max-h-[80vh] w-auto rounded-xl border border-white/10 object-contain" onClick={(e) => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default VentureDetail;
